class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.handleSubmit = this.handleSubmit.bind(this); // Bind once
  }

  connectedCallback() {
    this.form.addEventListener('submit', this.handleSubmit);
  }

  disconnectedCallback() {
    this.form.removeEventListener('submit', this.handleSubmit);
  }

  async handleSubmit(e) {
    e.preventDefault();

    const formData = new FormData(e.target);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || '1', 10);

    if (!variantId) return;

    const items = [{ id: variantId, quantity }];

    try {
      // Show loading state
      this.setLoadingState(true);

      const res = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        body: JSON.stringify({ items }),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (res.status === 422) {
        const { errors } = await res.json();
        const errorMessage = Array.isArray(errors)
          ? errors.join(', ')
          : 'Unable to add item to cart. Please try again.';
        
        // Announce error to screen reader
        if (window.announceToScreenReader) {
          window.announceToScreenReader(`Error: ${errorMessage}`, 'assertive');
        }
        
        this.showError(errorMessage);
        return;
      }

      if (!res.ok) {
        const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
        error.status = res.status;
        throw error;
      }

      if (res.ok) {
        const cartData = await res.json();

        // Use CartUtils cleanup logic
        const latestCart = await window.CartUtils.fetchCart();
        if (window.CartUtils.hasSubscriptionAttributes(latestCart)) {
          await window.CartUtils.removeSubscriptionProducts(latestCart);
        } else {
          await window.CartUtils.clearSubscriptionAttributesIfNoSubsExist();
        }

        // Get the added product data for the modal
        const addedItem = latestCart.items.find((item) => item.variant_id == variantId);

        if (addedItem) {
          // Update cart count bubble immediately
          if (window.CartCountUpdater) {
            window.CartCountUpdater.updateCartIcon(latestCart);
          }

          // Dispatch cart updated event for progress bar updates
          document.dispatchEvent(new CustomEvent('cart:updated'));
          // Announce to screen reader
          if (window.announceToScreenReader) {
            window.announceToScreenReader(`${addedItem.product_title} added to cart`);
          }

          await this.showCartAdditionModal(addedItem, latestCart);
        }
      }
    } catch (error) {
      console.error('Unexpected error adding to cart:', error);
      const errorMessage = window.ErrorHandler
        ? window.ErrorHandler.handleError(error, 'cart', false)
        : 'Something went wrong. Please check your connection and try again.';
      
      // Announce error to screen reader
      if (window.announceToScreenReader) {
        window.announceToScreenReader(`Error: ${errorMessage}`, 'assertive');
      }
      
      this.showError(errorMessage);
    } finally {
      this.setLoadingState(false);
    }
  }

  async showCartAdditionModal(addedItem, cartData) {
    // Get the modal element from the page (it should be rendered via liquid)
    let modal = document.querySelector('cart-addition-modal');
    if (!modal) {
      console.error('Cart addition modal not found in DOM');
      // Fallback to redirect if modal is not available
      window.location.href = window.routes.cart_url;
      return;
    }

    // Get inventory data from form attributes
    const inventoryManaged = this.form.dataset.inventoryManaged;
    const inventoryQuantity = parseInt(this.form.dataset.inventoryQuantity) || 0;
    const lowStockThreshold = parseInt(this.form.dataset.lowStockThreshold) || 20;
    const isLowStock = inventoryManaged && inventoryQuantity <= lowStockThreshold;

    // Get brand name from form attributes, fallback to product title
    const brandName = this.form.dataset.brandName || addedItem.product_title;

    // Construct product subtitle from form attributes
    const vintage = this.form.dataset.productVintage || '';
    const varietal = this.form.dataset.productVarietal || '';
    const region = this.form.dataset.productRegion || '';
    const country = this.form.dataset.productCountry || '';

    let productSubtitle = `${vintage} ${varietal}`.trim();
    if (region || country) {
      productSubtitle += ' from ';
      if (region) {
        productSubtitle += region;
        if (country) {
          productSubtitle += ', ';
        }
      }
      if (country) {
        productSubtitle += country;
      }
    }

    // Get product handle from the added item or form
    const productHandle = addedItem.handle || addedItem.product_handle || this.form.dataset.productHandle;

    // Prepare product data for the modal
    const productData = {
      title: brandName,
      subtitle: productSubtitle,
      handle: productHandle,
      variant: {
        id: addedItem.variant_id,
        title: addedItem.variant_title,
      },
      price: addedItem.price,
      discountedPrice: addedItem.discounted_price,
      quantity: addedItem.quantity,
      image: this.getProductImage(addedItem),
      isFineWine: this.isFineWineProduct(addedItem),
      isLowStock: isLowStock,
      inventoryQuantity: inventoryQuantity,
    };

    // Show the modal
    await modal.show(productData, cartData);
  }

  getVariantDescription(item) {
    if (!item.options_with_values || item.options_with_values.length === 0) {
      return '';
    }

    return item.options_with_values.map((option) => `${option.name}: ${option.value}`).join(', ');
  }

  isFineWineProduct(item) {
    // Check if this is a fine wine product based on the product type or metafields
    // This would need to be adjusted based on your specific product structure
    return (
      item.product_type === 'Fine Wine' ||
      (item.properties && item.properties['Fine Wine Collection']) ||
      (item.product && item.product.tags && item.product.tags.includes('fine-wine'))
    );
  }

  getProductImage(item) {
    if (!item.image) return '';

    // Convert to a smaller size for the modal
    return item.image.replace(/\.(jpg|jpeg|png|webp)$/i, '_100x.$1');
  }

  setLoadingState(isLoading) {
    const submitButton = this.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = isLoading;
      if (isLoading) {
        submitButton.classList.add('loading');
        this.showLoadingSpinner(submitButton);
      } else {
        submitButton.classList.remove('loading');
        this.hideLoadingSpinner(submitButton);
      }
    }
  }

  showLoadingSpinner(button) {
    // Find or create loading spinner
    let spinner = button.querySelector('.loading__spinner');
    if (!spinner) {
      spinner = document.createElement('div');
      spinner.className = 'loading__spinner';
      button.appendChild(spinner);
    }
    spinner.classList.remove('hidden');
  }

  hideLoadingSpinner(button) {
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
    }
  }

  showError(message) {
    // Remove any existing error messages
    this.hideError();

    // // Create error message
    // const errorWrapper = document.createElement('div');
    // errorWrapper.className = 'product-form__error-message-wrapper show';
    // errorWrapper.innerHTML = `
    //   <span class="svg-wrapper">
    //     <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    //       <path d="M8 6V10M8 14H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    //     </svg>
    //   </span>
    //   <span class="product-form__error-message">${message}</span>
    // `;

    // // Insert after the form
    // this.form.parentNode.insertBefore(errorWrapper, this.form.nextSibling);

    // Show toast notification
    if (window.ToastManager) {
      window.ToastManager.error('Add to Cart Failed', message);
    }

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.hideError();
    }, 5000);
  }

  hideError() {
    const errorWrapper = this.form.parentNode.querySelector('.product-form__error-message-wrapper');
    if (errorWrapper) {
      errorWrapper.remove();
    }
  }

  showSuccess(message) {
    if (window.ToastManager) {
      window.ToastManager.success('Added to Cart', message);
    }
  }

  showButtonSuccess() {
    const submitButton = this.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.classList.add('success');
      setTimeout(() => {
        submitButton.classList.remove('success');
      }, 2000);
    }
  }

}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
